/* Карточка «Ещё настройки» «Говорящего кружка» (APP-SPEC-4 §5): общая StudioSettings (BotCore.settingsCard) со слотами
   по тому, кто говорит: Голос, Формат, Вид персонажа, Зациклить, Движение, Фоновый звук. Лист один; секции по слотам:
   Голос — постоянные строки, голос каталога третьей строкой, «Другой голос ›» (полный выбор во втором слое того же листа,
   BotCore.voiceHost; у записи и своей речи — только голоса с переозвучкой); Формат — строки с формой кадра; Вид — круги-лица;
   Зациклить — строка-переключатель; Движение — текст с вкладками «Что происходит · Чего не должно»; Фоновый звук — текст с чипами. */
import { useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { endpoints } from '../../../api';
import type { Voice } from '../../../api/types';
import { uiText } from '../../../i18n';
import { ChoiceTiles } from '../../../ui/ChoiceTiles';
import { Seg } from '../../../ui/Seg';
import { SwitchRow } from '../../../ui/SwitchRow';
import { VoicePick } from '../../tts/VoicePick';
import { SettingsDrill } from '../SettingsDrill';
import { SettingsDrillContext } from '../settings-drill-context';
import { StudioIcon } from '../StudioIcon';
import { StudioPromptField } from '../StudioPromptField';
import { StudioSettings, StudioSettingsSection, type StudioSettingSlot } from '../StudioFormParts';
import { artworkUrl } from '../studio-art-model';
import { CIRCLE_MOTION_DO, CIRCLE_MOTION_NOT, circleFormatWord, circleRatio } from './circle-model';

export interface CircleVoiceRow {
  value: string;
  label: string;
  sub: string;
  /** Слово в слоте вместо подписи строки («Как в видео») */
  short?: string;
  image?: string | null;
}
export interface CircleVoiceConfig {
  rows: CircleVoiceRow[];
  /** value выбранной строки; голос каталога — `voice:<id>` */
  current: string;
  onPick(value: string): void;
  /** Голос каталога, если он не среди постоянных строк (третья строка) */
  other: Voice | null;
  /** Только голоса с переозвучкой */
  s2s: boolean;
  onOther(voice: Voice): void;
  /** Миниатюра слота */
  image?: string | null;
}
export interface CircleSettingsProps {
  disabled: boolean;
  voice?: CircleVoiceConfig | null;
  format?: { choices: Array<[string, string, string]>; current: string; onPick(value: string): void; locked?: string | null } | null;
  skin?: { skins: Array<{ id: string; name: string; image?: string | null }>; current: string; onPick(value: string): void } | null;
  loop?: { value: boolean; onChange(value: boolean): void } | null;
  motion?: { value: { do: string; not: string }; max: number; onChange(value: { do: string; not: string }): void } | null;
  sfx?: { value: string; max: number; presets: Array<[string, string]>; onChange(value: string): void } | null;
  /** Единственная ячейка строкой без карточки («Кружок из аудио» со своей записью) */
  oneRow?: boolean;
  /** Вход voicePick: лист сразу на секции «Голос» */
  openVoice?: boolean;
}
const shape = (value: string) => {
  if (value === 'circle')
    return (
      <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
        <circle cx="16" cy="16" r="11" />
      </svg>
    );
  const ratio = circleRatio(value);
  const w = Math.min(22, 22 * ratio),
    h = Math.min(22, 22 / ratio);
  return (
    <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <rect x={(32 - w) / 2} y={(32 - h) / 2} width={w} height={h} rx="3" />
    </svg>
  );
};
const face = (url?: string | null) => (artworkUrl(url) ? <img className="circle-sc-face" src={artworkUrl(url)!} alt="" /> : null);

/** Второй слой листа: полный выбор голоса BotCore.voiceHost.mount; без него — выбор голоса страницы (VoicePick). */
function OtherVoice({ value, s2s, disabled, onPick }: { value: Voice | null; s2s: boolean; disabled: boolean; onPick(voice: Voice): void }) {
  const drill = useContext(SettingsDrillContext);
  const mount = typeof window !== 'undefined' ? (window.BotCore?.voiceHost as { mount?: unknown } | undefined)?.mount : undefined;
  if (!drill || typeof mount !== 'function')
    return <VoicePick value={value} s2s={s2s} disabled={disabled} sub={s2s ? uiText('Только голоса с переозвучкой') : undefined} onPick={onPick} />;
  return (
    <>
      <button type="button" className="sc-r circle-other" disabled={disabled} onClick={() => drill.open('voice')}>
        <span className="sc-th o" aria-hidden="true">
          <StudioIcon kind="search" />
        </span>
        <span className="m">
          <b>{uiText('Другой голос')}</b>
          <small>{uiText(s2s ? 'Только голоса с переозвучкой' : 'Каталог голосов, подборки')}</small>
        </span>
        <span className="rd" aria-hidden="true">
          <StudioIcon kind="chev" />
        </span>
      </button>
      <SettingsDrill id="voice">
        <VoiceHostLayer value={value} s2s={s2s} onPick={voice => {
          onPick(voice);
          drill.back();
        }} onClose={drill.back} />
      </SettingsDrill>
    </>
  );
}
export function VoiceHostLayer({ value, s2s, onPick, onClose }: { value: Voice | null; s2s: boolean; onPick(voice: Voice): void; onClose(): void }) {
  const host = useRef<HTMLDivElement>(null);
  const [error, setError] = useState('');
  const latest = useRef({ onPick, onClose });
  useEffect(() => {
    latest.current = { onPick, onClose };
  });
  useEffect(() => {
    const container = host.current;
    const api = window.BotCore?.voiceHost as { mount?(el: HTMLElement, o: Record<string, unknown>): { destroy(): void } | null } | undefined;
    if (!container || !api?.mount) return;
    const parent = document.createElement('div');
    parent.className = 'tts-cat circle-vp';
    const el = document.createElement('div');
    el.id = 'circleVp';
    parent.append(el);
    container.append(parent);
    const request = { current: null as AbortController | null };
    /* voiceHost.mount собирает список через flushSync: из эффекта React его не выполняет («flushSync was called from inside
       a lifecycle method»), колонка не появляется и mount бросает «колонка #circleVp не собралась» — экран пустел.
       Поэтому монтируем следующей задачей, когда React уже вышел из фазы эффектов. */
    const mountPicker = () => api.mount!(el, {
      value: value?.id ?? null,
      only: s2s ? 's2s' : '',
      closeLabel: uiText('К настройкам'),
      onClose: () => latest.current.onClose(),
      onPick: (id: number) => {
        request.current?.abort();
        const controller = new AbortController();
        request.current = controller;
        void endpoints
          .voiceDetails(id, controller.signal)
          .then(voice => {
            if (controller.signal.aborted) return;
            if (voice.id !== id) throw new Error('voice_mismatch');
            if (s2s && !voice.is_s2s) throw new Error('voice_not_s2s');
            latest.current.onPick(voice);
          })
          .catch(failure => {
            if (!controller.signal.aborted)
              setError(failure instanceof Error && failure.message === 'voice_not_s2s' ? 'Этот голос не умеет переозвучку записи. Выберите другой.' : 'Не удалось выбрать голос. Попробуйте ещё раз.');
          });
      },
    });
    let picker: { destroy(): void } | null = null;
    let cancelled = false;
    const timer = window.setTimeout(() => {
      if (cancelled) return;
      try {
        picker = mountPicker();
      } catch {
        setError('Не удалось открыть список голосов. Попробуйте ещё раз.');
      }
    });
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      request.current?.abort();
      picker?.destroy();
      parent.remove();
    };
    // Слой монтируется один раз на открытие; выбранный голос — начальное значение списка.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s2s]);
  return (
    <div className="circle-voice-layer">
      <div ref={host} />
      {error && <p role="alert">{uiText(error)}</p>}
    </div>
  );
}

export function CircleSettingsCard(props: CircleSettingsProps) {
  const { disabled, voice, format, skin, loop, motion, sfx } = props;
  const [tab, setTab] = useState<'do' | 'not'>('do');
  const root = useRef<HTMLDivElement>(null);
  const opened = useRef(false);
  useEffect(() => {
    if (!props.openVoice || opened.current || !voice) return;
    opened.current = true;
    const frame = requestAnimationFrame(() => root.current?.querySelector<HTMLButtonElement>('[data-sc-cell="voice"]')?.click());
    return () => cancelAnimationFrame(frame);
  }, [props.openVoice, voice]);
  const voiceRow = voice ? voice.rows.find(row => row.value === voice.current) : undefined;
  const slots: StudioSettingSlot[] = [];
  if (voice)
    slots.push({
      title: 'Голос',
      section: 'voice',
      value: voiceRow?.short ?? voiceRow?.label ?? voice.other?.name ?? 'Выберите голос',
      thumb: face(voiceRow?.image ?? voice.image) ?? <StudioIcon kind="voice" />,
    });
  if (format)
    slots.push(
      format.locked
        ? { title: 'Формат', section: 'fmt', kind: 'info', lock: true, tip: format.locked, value: circleFormatWord(format.current), thumb: shape(format.current) }
        : { title: 'Формат', section: 'fmt', value: circleFormatWord(format.current), thumb: shape(format.current) },
    );
  if (skin) {
    const picked = skin.skins.find(item => item.id === skin.current) ?? skin.skins[0];
    slots.push({ title: 'Вид персонажа', section: 'skin', value: picked?.name ?? '', thumb: face(picked?.image) ?? undefined });
  }
  if (loop) slots.push({ title: 'Зациклить', section: 'loop', kind: 'toggle', value: loop.value ? 'Включено' : 'Выключено' });
  if (motion) {
    const summary = (motion.value.do.trim() || motion.value.not.trim()).replace(/\s+/g, ' ');
    slots.push({ title: 'Движение', section: 'mv', kind: 'text', value: summary, empty: !summary });
  }
  if (sfx) slots.push({ title: 'Фоновый звук', section: 'sfx', kind: 'text', value: sfx.value.trim(), empty: !sfx.value.trim() });
  if (!slots.length) return null;
  const sections: ReactNode[] = [];
  if (voice)
    sections.push(
      <StudioSettingsSection key="voice" id="voice" title="Голос" kind="voice">
        {!!voice.rows.length && (
          <ChoiceTiles
            label={uiText('Голос')}
            variant="rows"
            value={voice.current}
            disabled={disabled}
            onChange={voice.onPick}
            choices={[
              ...voice.rows.map(row => ({ value: row.value, label: uiText(row.label), description: uiText(row.sub), icon: face(row.image) ?? <StudioIcon kind={row.value === 'src' || row.value === 'keep' ? 'wave' : 'voice'} /> })),
              ...(voice.other && !voice.rows.some(row => row.value === `voice:${voice.other!.id}`)
                ? [{ value: `voice:${voice.other.id}`, label: voice.other.name, description: uiText('Из каталога'), icon: <StudioIcon kind="voice" /> }]
                : []),
            ]}
          />
        )}
        <OtherVoice value={voice.other} s2s={voice.s2s} disabled={disabled} onPick={voice.onOther} />
      </StudioSettingsSection>,
    );
  if (format && !format.locked)
    sections.push(
      <StudioSettingsSection key="fmt" id="fmt" title="Формат" kind="choice">
        <ChoiceTiles
          label={uiText('Формат')}
          variant="rows"
          value={format.current}
          disabled={disabled}
          onChange={format.onPick}
          choices={format.choices.map(([value, label, description]) => ({ value, label: uiText(label), description: uiText(description), icon: shape(value) }))}
        />
      </StudioSettingsSection>,
    );
  if (skin)
    sections.push(
      <StudioSettingsSection key="skin" id="skin" title="Вид персонажа" kind="faces">
        <div className="circle-faces" role="group" aria-label={uiText('Вид персонажа')}>
          {skin.skins.map(item => (
            <button
              key={item.id}
              type="button"
              className={`circle-face${item.id === skin.current ? ' on' : ''}`}
              aria-pressed={item.id === skin.current}
              disabled={disabled}
              onClick={() => skin.onPick(item.id)}
            >
              <span className="circle-face-pic">{face(item.image) ?? <StudioIcon kind="user" />}</span>
              <b>{item.name}</b>
            </button>
          ))}
        </div>
      </StudioSettingsSection>,
    );
  if (loop)
    sections.push(
      <StudioSettingsSection key="loop" id="loop" kind="toggle">
        <SwitchRow
          title={uiText('Зациклить видео')}
          description={uiText('Видео повторится, пока звучит речь')}
          icon={<StudioIcon kind="video" />}
          checked={loop.value}
          disabled={disabled}
          onChange={loop.onChange}
        />
      </StudioSettingsSection>,
    );
  if (motion)
    sections.push(
      <StudioSettingsSection key="mv" id="mv" title="Движение" hint="необязательно" kind="text">
        <Seg
          items={[
            { key: 'do', label: uiText('Что происходит') },
            { key: 'not', label: uiText('Чего не должно') },
          ]}
          value={tab}
          onChange={value => setTab(value as 'do' | 'not')}
          label={uiText('Движение')}
        />
        <StudioPromptField
          key={tab}
          label={tab === 'do' ? 'Что происходит' : 'Чего не должно'}
          value={motion.value[tab]}
          onChange={value => motion.onChange({ ...motion.value, [tab]: value })}
          minimum={0}
          limit={motion.max}
          disabled={disabled}
          presets={tab === 'do' ? CIRCLE_MOTION_DO : CIRCLE_MOTION_NOT}
          improvement={false}
          kind="video"
          note="Движение сгенерирует модель STEOS"
          placeholder={tab === 'do' ? 'Как оживить снимок? Например: моргает, улыбается, поворачивает голову' : 'Чего не должно быть в кадре? Например: лишних рук, искажений лица'}
        />
      </StudioSettingsSection>,
    );
  if (sfx)
    sections.push(
      <StudioSettingsSection key="sfx" id="sfx" title="Фоновый звук" hint="необязательно" kind="text">
        <StudioPromptField
          label="Фоновый звук"
          value={sfx.value}
          onChange={sfx.onChange}
          minimum={0}
          limit={sfx.max}
          disabled={disabled}
          presets={sfx.presets}
          improvement={false}
          kind="video"
          note="Звук сгенерирует модель STEOS"
          placeholder="Что звучит вокруг? Дождь, город, костёр"
        />
      </StudioSettingsSection>,
    );
  return (
    <div ref={root} className="circle-settings">
      <StudioSettings slots={slots} disabled={disabled} oneRow={props.oneRow}>
        {sections}
      </StudioSettings>
    </div>
  );
}
